// ** Utils
import spacing from '@/theme/settings/spacing';
import breakpoints from '@/theme/settings/breakpoints';

// ** Types
import type { Theme, ThemeOptions } from '@mui/material/styles';

// #FFFFFF white
// #662046 purple dark
// #882345 purple light
// #AA272F light red
// #DE3831 lighter red
// #82786F grey
// #1E1E1E dark grey
// #DF7A00 orange

export const lightTheme: ThemeOptions = {
	breakpoints,
	layout: {
		pagePx: { xs: 4, md: 10, lg: 20 },
		twoColMinWidthPx: 1120,
		desktopGutter: 20,
		desktopColumnGap: { xs: 28, sm: 26, lg: 24 },
		heroPanelPx: { xs: 4, sm: 8, lg: 16 },
		contentMaxWidthPx: 1440,
		heroPanelContentMaxWidthPx: 592
	},
	palette: {
		mode: 'light',
		primary: {
			lighter: '#F0AB00',
			light: '#E37222',
			main: '#DF7A00',
			dark: '#D0661C',
			contrastText: '#FFF'
		},
		secondary: {
			light: '#882345',
			main: '#662046'
			// dark: '#7900A8',
			// contrastText: '#600085'
		},
		error: {
			light: '#DE3831',
			main: '#CC060C'
			// dark: '#CF000F',
			// contrastText: '#FF3366'
		},
		// warning: {
		// 	light: '#F9E154',
		// 	main: '#F2930C',
		// 	dark: '#F0541E',
		// 	contrastText: '#F0AD4E'
		// },
		// info: {
		// 	light: '#27ACE2',
		// 	main: '#0000FF',
		// 	dark: '#0B4A8F',
		// 	contrastText: '#4186C6'
		// },
		success: {
			light: '#3FBD40',
			main: '#4B9609',
			dark: '#2D5D07'
			// contrastText: '#22BB33'
		},
		text: {
			primary: '#1E1E1E',
			secondary: '#353535',
			tertiary: '#706F6F',
			disabled: '#C0C0C0'
		},
		common: {
			black: '#000000',
			white: '#FFFFFF'
		},
		// contrastThreshold: 2,
		// tonalOffset: {
		// 	light: 0.5,
		// 	dark: 0.5
		// },
		grey: {
			50: '#F8F8FA', // Lightest
			100: '#F3F2F1', // Very Light (Example: User Dashboard)
			200: '#E5E6EB', // Light
			300: '#D2D4DC', // Light-Medium
			400: '#C0C2CE', // Medium
			500: '#AFAFAF', // Medium-Dark
			600: '#82786F', // Dark (Example: Association Teams Chip)
			700: '#5B5B5B', // Darker
			800: '#484848', // Darker
			900: '#3A3A3A', // Darkest
			A100: '#EEEEEE', // Accent Light
			A200: '#CCCCCC', // Accent Medium-Light
			A400: '#666666', // Accent Medium-Dark
			A700: '#333333' // Accent Dark
		},
		divider: '#0000002E',
		softGrey: {
			main: '#A3A3A3'
		},
		// action: {
		// 	// active: '',
		// 	hover: '#FF6600',
		// 	hoverOpacity: 0.5,
		// 	// selected: '',
		// 	selectedOpacity: 0.5,
		// 	// disabled: '',
		// 	disabledOpacity: 0.5,
		// 	// disabledBackground: '',
		// 	// focus: '',
		// 	focusOpacity: 0.5,
		// 	activatedOpacity: 0.5
		// },
		// background: {
		// 	paper: '#DF7A00'
		// }
		generalBackground: {
			main: '#FFFFFF'
		},

		// Warm off-white used by hero header "light" variant panel.
		heroLightBackground: {
			main: '#F6F4F2'
		},
		selectHoverText: {
			main: '#E7EDE6'
		},
		loginOverlayCta: {
			main: '#82786F',
			dark: '#786F67',
			contrastText: '#FFFFFF'
		},
		externalLink: {
			main: '#0000EE'
		}
	},
	...spacing,
	// ...shadows('light'),
	typography: {
		h1: undefined,
		h2: undefined,
		h3: undefined,
		h4: undefined,
		h5: undefined,
		h6: undefined,
		subtitle1: undefined,
		subtitle2: undefined,
		body1: undefined,
		body2: undefined,
		button: undefined,
		// caption is defined below as part of the new type scale

		// H1
		'h1-normal': {
			fontWeight: 'bolder',
			textTransform: 'uppercase',
			[breakpoints.down('sm')]: {
				// xs breakpoint
				fontSize: 35
			},
			[breakpoints.up('md')]: {
				// xl breakpoint
				fontSize: 65
			}
		},
		'h1-primary': {
			fontWeight: 'bolder',
			color: '#DF7A00',
			textTransform: 'uppercase',
			[breakpoints.down('sm')]: {
				// xs breakpoint
				fontSize: 35
			},
			[breakpoints.up('md')]: {
				// xl breakpoint
				fontSize: 65
			}
		},

		// H2
		'h2-normal': {
			fontWeight: 'bold',
			textTransform: 'uppercase',
			[breakpoints.down('sm')]: {
				// xs breakpoint
				fontSize: 27
			},
			[breakpoints.up('md')]: {
				// xl breakpoint
				fontSize: 60
			}
		},
		'h2-primary': {
			fontWeight: 'bold',
			color: '#DF7A00',
			textTransform: 'uppercase',
			[breakpoints.down('sm')]: {
				// xs breakpoint
				fontSize: 27
			},
			[breakpoints.up('md')]: {
				// xl breakpoint
				fontSize: 60
			}
		},

		// H3
		'h3-normal': {
			fontWeight: 'bold',
			textTransform: 'uppercase',
			[breakpoints.down('sm')]: {
				// xs breakpoint
				fontSize: 25
			},
			[breakpoints.up('md')]: {
				// xl breakpoint
				fontSize: 48
			}
		},
		'h3-primary': {
			fontWeight: 'bold',
			color: '#DF7A00',
			textTransform: 'uppercase',
			[breakpoints.down('sm')]: {
				// xs breakpoint
				fontSize: 25
			},
			[breakpoints.up('md')]: {
				// xl breakpoint
				fontSize: 48
			}
		},

		// H4
		'h4-normal': {
			fontWeight: 'bold',
			[breakpoints.down('sm')]: {
				// xs breakpoint
				fontSize: 24
			},
			[breakpoints.up('md')]: {
				// xl breakpoint
				fontSize: 32
			}
		},
		'h4-primary': {
			fontWeight: 'bold',
			color: '#DF7A00',
			overflowWrap: 'anywhere',
			[breakpoints.down('sm')]: {
				// xs breakpoint
				fontSize: 24
			},
			[breakpoints.up('md')]: {
				// xl breakpoint
				fontSize: 32
			}
		},

		// H5
		'h5-normal': {
			fontWeight: 'bold',
			[breakpoints.down('sm')]: {
				// xs breakpoint
				fontSize: 22
			},
			[breakpoints.up('md')]: {
				// xl breakpoint
				fontSize: 24
			}
		},
		'h5-primary': {
			fontWeight: 'bold',
			color: '#DF7A00',
			overflowWrap: 'anywhere',
			[breakpoints.down('sm')]: {
				// xs breakpoint
				fontSize: 22
			},
			[breakpoints.up('md')]: {
				// xl breakpoint
				fontSize: 24
			}
		},

		// H6
		'h6-normal': {
			fontSize: 20,
			fontWeight: 'bold'
		},
		'h6-primary': {
			fontSize: 20,
			fontWeight: 'bold',
			color: '#DF7A00',
			overflowWrap: 'anywhere'
		},

		// Subheadline
		'subheadline-normal': {
			fontSize: 17,
			fontWeight: 'bold'
		},
		'subheadline-primary': {
			fontSize: 17,
			fontWeight: 'bold',
			color: '#DF7A00'
		},

		// Button
		'button-normal': {
			fontSize: 18,
			fontWeight: 'bold'
		},
		'button-primary': {
			fontSize: 18,
			color: '#DF7A00',
			fontWeight: 'bold'
		},

		// New type scale variants
		'display-lg': {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: 'clamp(2.5rem, 2.25rem + 1.25vw, 3rem)',
			fontWeight: 500,
			lineHeight: 'clamp(2.875rem, 2.625rem + 1.25vw, 3.375rem)',
			letterSpacing: '-0.64px',
			whiteSpace: 'pre-line',
			[breakpoints.up('lg')]: { letterSpacing: '-0.96px' }
		},
		'display-md': {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: 'clamp(2.5rem, 2.25rem + 1.25vw, 3rem)',
			fontWeight: 400,
			lineHeight: 'clamp(2.875rem, 2.625rem + 1.25vw, 3.375rem)',
			letterSpacing: 'clamp(-0.048rem, -0.12vw - 0.019rem, -0.04rem)',
			whiteSpace: 'pre-line'
		},
		'title-lg': {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: '2.5rem',
			fontWeight: 400,
			lineHeight: '2.875rem',
			letterSpacing: '-1.2px',
			[breakpoints.up('md')]: {
				fontSize: '3rem',
				lineHeight: '3.375rem',
				letterSpacing: '-1.44px'
			}
		},
		'title-md': {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: '1.5rem',
			fontWeight: 400,
			lineHeight: '1.875rem',
			letterSpacing: '-0.48px',
			[breakpoints.up('lg')]: {
				fontSize: '2rem',
				lineHeight: '2.375rem',
				letterSpacing: '-0.64px'
			}
		},
		'title-sm': {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: 'clamp(1rem, 0.75rem + 1.25vw, 1.5rem)',
			fontWeight: 400,
			lineHeight: 'clamp(1.375rem, 0.8125rem + 1.5625vw, 2rem)',
			letterSpacing: 'clamp(-0.672px, -0.0233vw - 0.373px, -0.448px)'
		},
		'body-md': {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: 'clamp(1rem, 0.9554rem + 0.2232vw, 1.125rem)',
			fontWeight: 400,
			lineHeight: 'clamp(1.375rem, 1.2857rem + 0.4464vw, 1.625rem)',
			letterSpacing: '-0.05px'
		},
		'body-sm': {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: '1rem',
			fontWeight: 400,
			lineHeight: '1.375rem',
			letterSpacing: '-0.05px'
		},
		'body-lg': {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: 'clamp(1rem, 0.9108rem + 0.4464vw, 1.25rem)',
			fontWeight: 400,
			lineHeight: 'clamp(1.375rem, 1.1964rem + 0.8929vw, 1.875rem)',
			letterSpacing: '-0.05px'
		},
		label: {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: '0.875rem',
			fontWeight: 400,
			lineHeight: '1.25rem',
			letterSpacing: '-0.05px'
		},
		caption: {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: '0.75rem',
			fontWeight: 400,
			lineHeight: '1rem',
			letterSpacing: '-0.05px',
			[breakpoints.up('md')]: { fontSize: '0.875rem', lineHeight: '1.125rem' }
		},
		'panel-heading': {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: '1rem',
			fontWeight: 500,
			lineHeight: '1.375rem',
			letterSpacing: '-0.05px',
			[breakpoints.up('md')]: {
				fontSize: '1.125rem',
				lineHeight: '2rem'
			}
		},
		'body-xl': {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: '1.25rem',
			fontWeight: 400,
			lineHeight: '1.75rem',
			letterSpacing: '-0.01px'
		},
		nav: {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: '1.5rem',
			fontStyle: 'normal',
			fontWeight: 300,
			lineHeight: '2rem',
			letterSpacing: '-0.012px',
			[breakpoints.up('md')]: {
				fontWeight: 400
			}
		},
		subnav: {
			fontFamily: '"DIN Next LT Pro"',
			fontSize: '1.25rem',
			fontStyle: 'normal',
			fontWeight: 300,
			lineHeight: '1.5rem',
			letterSpacing: '-0.01px',
			[breakpoints.up('md')]: {
				fontSize: '1rem',
				fontWeight: 400,
				lineHeight: '1.375rem',
				letterSpacing: '-0.05px'
			}
		}
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				body: {
					backgroundColor: (theme: Theme) => {
						return theme.palette.generalBackground.main || '#FFFFFF'; // Fallback to white if undefined
					}
				}
			}
		},
		// MuiFormControl: {
		// 	styleOverrides: {
		// 		root: {
		// 			marginTop: 16,
		// 			marginBottom: 8
		// 		}
		// 	}
		// },
		MuiButton: {
			styleOverrides: {
				root: {
					minHeight: 30.75
				}
			},
			variants: [
				{
					props: { variant: 'outlined', color: 'primary' },
					style: {
						borderRadius: 0,
						border: '2.5px solid',
						position: 'relative',
						overflow: 'hidden',
						transition: 'color 0.3s ease',
						borderColor: '#DF7A00',
						zIndex: 2,
						'&:before': {
							content: '""',
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							background: '#DF7A00',
							zIndex: -1,
							transform: 'translateY(-100%)',
							transition: 'transform 0.3s ease'
						},
						'&:hover:before': {
							transform: 'translateY(0)'
						},
						'&:hover': {
							border: '2.5px solid',
							color: '#FFF',
							borderColor: '#DF7A00'
						},
						'&:after': {
							content: '""',
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							background: 'transparent',
							zIndex: 2,
							transition: 'transform 0.3s ease'
						},
						'&:hover:after': {
							transform: 'translateY(100%)'
						},
						'& span': {
							position: 'relative',
							fontWeight: 900,
							zIndex: 2
						}
					}
				}
			]
		},
		MuiTypography: {
			styleOverrides: {
				root: {
					overflowWrap: 'anywhere'
				}
			},
			defaultProps: {
				variantMapping: {
					// Map the new variant to render a <h1> by default
					'h1-normal': 'h1',
					'h1-primary': 'h1',
					'h2-normal': 'h2',
					'h2-primary': 'h2',
					'h3-normal': 'h3',
					'h3-primary': 'h3',
					'h4-normal': 'h4',
					'h4-primary': 'h4',
					'h5-normal': 'h5',
					'h5-primary': 'h5',
					'h6-normal': 'h6',
					'h6-primary': 'h6',
					'subheadline-normal': 'p',
					'subheadline-primary': 'p',
					'button-normal': 'p',
					'button-primary': 'p',

					// new type scale
					'display-lg': 'h1',
					'display-md': 'h2',
					'title-lg': 'h1',
					'title-md': 'h2',
					'title-sm': 'h3',
					'body-md': 'p',
					'body-sm': 'p',
					'body-lg': 'p',
					label: 'p',
					caption: 'small',
					'panel-heading': 'h2',
					'body-xl': 'p',
					nav: 'h3',
					subnav: 'span'
				}
			}
		},
		MuiAccordion: {
			styleOverrides: {
				root: {
					maxWidth: 1088,
					marginBottom: 3,
					background: '#DF7A00',
					'&:not(.Mui-expanded):hover': {
						background: '#DF7A0099'
					},
					'&:first-of-type': {
						borderTopLeftRadius: 10,
						borderTopRightRadius: 10
					},
					'&:last-of-type': {
						borderBottomLeftRadius: 10,
						borderBottomRightRadius: 10
					}
				}
			}
		},
		MuiAccordionSummary: {
			styleOverrides: {
				root: {
					color: '#FFF',
					padding: '10px 25px;'
				},
				content: {
					'&.Mui-expanded': {
						margin: '12px 0'
					}
				}
			}
		},
		MuiAccordionDetails: {
			styleOverrides: {
				root: {
					color: '#FFF',
					padding: '0px 25px 20px 25px'
				}
			}
		},
		MuiPaginationItem: {
			styleOverrides: {
				root: {
					color: '#1E1E1E',
					'&.Mui-selected': {
						backgroundColor: '#1E1E1E',
						color: '#FFFFFF',
						'&:hover': {
							backgroundColor: '#353535'
						}
					}
				}
			}
		},
		MuiCard: {
			styleOverrides: {
				root: {
					borderRadius: 0
				}
			}
		}
	}
};
